import { useEffect, useRef } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import type { PlayRound } from '../api/client'
import { isRedSuit, rankLabel, suitSymbol } from '../api/client'
import { SPRING_POP, EASE_OUT } from '../../../shared/motion.ts'

type Props = {
  result: PlayRound | null
  onClose: () => void
}

const BURST_COLORS = ['#4ecdc4', '#3ddc97', '#dce4ff', '#8ef0c2']

/** One-shot celebratory dot burst from the result card center (win only). */
function WinBurst() {
  const dots = 12
  return (
    <div className="win-burst" aria-hidden>
      {Array.from({ length: dots }, (_, i) => {
        const a = (i / dots) * Math.PI * 2
        const r = 72 + (i % 3) * 22
        return (
          <motion.span
            key={i}
            className="burst-dot"
            style={{ background: BURST_COLORS[i % BURST_COLORS.length] }}
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            animate={{
              x: Math.cos(a) * r,
              y: Math.sin(a) * r - 24,
              opacity: 0,
              scale: 0.3,
            }}
            transition={{ duration: 0.7, delay: i * 0.015, ease: EASE_OUT }}
          />
        )
      })}
    </div>
  )
}

export function ResultOverlay({ result, onClose }: Props) {
  const { t } = useTranslation()
  const reduce = useReducedMotion()
  const dialogRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!result) return
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
    dialogRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      previous?.focus()
    }
  }, [result])

  return (
    <AnimatePresence>
      {result && (
        <motion.div
          className="overlay"
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            ref={dialogRef}
            className={`result-card ${result.won ? 'win' : 'lose'}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="hilo-result-title"
            tabIndex={-1}
            initial={reduce ? { opacity: 0 } : { scale: 0.85, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={SPRING_POP}
            onClick={(e) => e.stopPropagation()}
          >
            {result.won && !reduce && <WinBurst />}
            <h2 id="hilo-result-title">{result.won ? t('win') : t('lose')}</h2>
            <p
              className="result-cards"
              aria-label={`${rankLabel(result.base_card)}${suitSymbol(result.base_suit)} → ${rankLabel(result.next_card)}${suitSymbol(result.next_suit)}`}
            >
              <span className={`result-card-token ${isRedSuit(result.base_suit) ? 'red' : 'black'}`} aria-hidden>
                <span className="card-rank">{rankLabel(result.base_card)}</span>
                <span className="card-suit">{suitSymbol(result.base_suit)}</span>
              </span>
              <span className="result-card-arrow" aria-hidden>→</span>
              <span className={`result-card-token ${isRedSuit(result.next_suit) ? 'red' : 'black'}`} aria-hidden>
                <span className="card-rank">{rankLabel(result.next_card)}</span>
                <span className="card-suit">{suitSymbol(result.next_suit)}</span>
              </span>
            </p>
            <p className="detail">
              {t('choiceLine', {
                choice: result.choice === 'higher' ? t('higher') : t('lower'),
                odds: result.odds.toFixed(3),
              })}
            </p>
            <p className="payout">
              {result.won
                ? t('payoutWin', { amount: result.payout })
                : t('payoutLose', { amount: result.bet_amount })}
            </p>
            <button type="button" className="primary" onClick={onClose}>
              {t('again')}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
